import React from 'react';

import {
  CrmSyncAccountDefaultFieldIdsEnum,
  CrmSyncCardDefaultFieldIdsEnum,
  CrmSyncIntegrationResourceTypesEnum,
} from '../../../../../../../../../src/models/api/crmSyncModel';
import {
  CustomFieldModel,
  CustomFieldResourceTypesEnum,
  CustomFieldsEnum,
} from '../../../../../../../../../src/models/api/customFieldModel';
import {reportToBugsnag} from '../../../../../../../../../srcCommon/services/bugsnag/bugsnag';
import {Trans} from '../../../../../../../../shared/helpers/src/internationalization/react';
import {ErrorWithData} from '../../../../../../../../shared/helpers/src/types/customErrors';
import {IconName} from '../../../../../../../../shared/ui/src/components/icon/icon';

/*
 * Constants.
 */

export const defaultIcons: {[key: string]: IconName} = {
  email: 'messageSmall',
  phone: 'phoneSmall',
  account: 'newAccountSmall',
};

export const maxAccountsToSync = 150000; // 150 thousand
export const maxCardsToSync = 1000000; // 1 million

/*
 * Default Account Field Types.
 */

enum CrmSyncAccountDefaultFieldTypesEnum {
  NAME = 'account.name',
  DOMAINS = 'account.domains',
  DESCRIPTION = 'account.description',
}

// All these fields are not needed since we will not be using this as a real custom field.
// This is only used for the sync mapping dropdown
const partialCustomFieldNotNeededFields: Omit<CustomFieldModel, 'type' | 'id' | 'name' | 'resourceType'> = {
  description: '',
  enumValues: undefined,
  url: '',
  nameSlug: '',
  origin: '',
  createdAt: 0,
  updatedAt: 0,
};

const defaultAccountCustomFields: ReadonlyArray<CustomFieldModel> = [
  {
    resourceType: CustomFieldResourceTypesEnum.ACCOUNT,
    id: CrmSyncAccountDefaultFieldIdsEnum.NAME,
    type: CustomFieldsEnum.STRING,
    name: 'name',
    ...partialCustomFieldNotNeededFields,
  },
  {
    resourceType: CustomFieldResourceTypesEnum.ACCOUNT,
    id: CrmSyncAccountDefaultFieldIdsEnum.DOMAINS,
    type: CustomFieldsEnum.EMAIL_DOMAIN,
    name: 'email domains',
    ...partialCustomFieldNotNeededFields,
  },
  {
    resourceType: CustomFieldResourceTypesEnum.ACCOUNT,
    id: CrmSyncAccountDefaultFieldIdsEnum.DESCRIPTION,
    type: CustomFieldsEnum.STRING,
    name: 'description',
    ...partialCustomFieldNotNeededFields,
  },
];

/*
 * Default Card Field Types.
 */

export enum CrmSyncCardDefaultFieldTypesEnum {
  NAME = 'card.name',
  EMAIL = 'card.email',
  PHONE = 'card.phone',
  ACCOUNT_ID = 'card.accountId',
  BIO = 'card.bio',
}

const defaultCardCustomFields: ReadonlyArray<CustomFieldModel> = [
  {
    resourceType: CustomFieldResourceTypesEnum.CARD,
    id: CrmSyncCardDefaultFieldIdsEnum.NAME,
    type: CustomFieldsEnum.STRING,
    name: 'name',
    ...partialCustomFieldNotNeededFields,
  },
  {
    resourceType: CustomFieldResourceTypesEnum.CARD,
    id: CrmSyncCardDefaultFieldIdsEnum.EMAIL,
    type: CustomFieldsEnum.EMAIL_DOMAIN,
    name: 'email',
    ...partialCustomFieldNotNeededFields,
  },
  {
    resourceType: CustomFieldResourceTypesEnum.CARD,
    id: CrmSyncCardDefaultFieldIdsEnum.PHONE,
    type: CustomFieldsEnum.STRING,
    name: 'phone',
    ...partialCustomFieldNotNeededFields,
  },
  {
    resourceType: CustomFieldResourceTypesEnum.CARD,
    id: CrmSyncCardDefaultFieldIdsEnum.ACCOUNT_ID,
    type: CustomFieldsEnum.STRING,
    name: 'accountId',
    ...partialCustomFieldNotNeededFields,
  },
  {
    resourceType: CustomFieldResourceTypesEnum.CARD,
    id: CrmSyncCardDefaultFieldIdsEnum.BIO,
    type: CustomFieldsEnum.STRING,
    name: 'bio',
    ...partialCustomFieldNotNeededFields,
  },
];

// these are special fields that can be added to the mapping multiple times
export function computeMultiAddFieldIds(resourceType: CrmSyncIntegrationResourceTypesEnum) {
  if (resourceType === CrmSyncIntegrationResourceTypesEnum.CARD) {
    return [CrmSyncCardDefaultFieldIdsEnum.EMAIL, CrmSyncCardDefaultFieldIdsEnum.PHONE];
  }
  return [];
}

/*
 * Helpers.
 */

export function computeCrmSyncDefaultCustomFields(resourceType: CrmSyncIntegrationResourceTypesEnum) {
  if (resourceType === CrmSyncIntegrationResourceTypesEnum.ACCOUNT) {
    return defaultAccountCustomFields;
  }

  return defaultCardCustomFields;
}

export function computeCrmSyncDefaultFieldType(
  frontFieldId: number,
  resourceType: CrmSyncIntegrationResourceTypesEnum,
) {
  if (resourceType === CrmSyncIntegrationResourceTypesEnum.ACCOUNT) {
    return computeCrmSyncAccountDefaultFieldType(frontFieldId);
  }
  return computeCrmSyncCardDefaultFieldType(frontFieldId);
}

function computeCrmSyncAccountDefaultFieldType(frontFieldId: number) {
  switch (frontFieldId) {
    case CrmSyncAccountDefaultFieldIdsEnum.NAME:
      return CrmSyncAccountDefaultFieldTypesEnum.NAME;
    case CrmSyncAccountDefaultFieldIdsEnum.DOMAINS:
      return CrmSyncAccountDefaultFieldTypesEnum.DOMAINS;
    case CrmSyncAccountDefaultFieldIdsEnum.DESCRIPTION:
      return CrmSyncAccountDefaultFieldTypesEnum.DESCRIPTION;
    default:
      return undefined;
  }
}

function computeCrmSyncCardDefaultFieldType(frontFieldId: number) {
  switch (frontFieldId) {
    case CrmSyncCardDefaultFieldIdsEnum.NAME:
      return CrmSyncCardDefaultFieldTypesEnum.NAME;
    case CrmSyncCardDefaultFieldIdsEnum.EMAIL:
      return CrmSyncCardDefaultFieldTypesEnum.EMAIL;
    case CrmSyncCardDefaultFieldIdsEnum.PHONE:
      return CrmSyncCardDefaultFieldTypesEnum.PHONE;
    case CrmSyncCardDefaultFieldIdsEnum.ACCOUNT_ID:
      return CrmSyncCardDefaultFieldTypesEnum.ACCOUNT_ID;
    case CrmSyncCardDefaultFieldIdsEnum.BIO:
      return CrmSyncCardDefaultFieldTypesEnum.BIO;
    default:
      return undefined;
  }
}

export function computeFrontAccountDefaultFieldIcon(frontFieldId?: number) {
  // We are rendering a default field that is not a true custom field.
  switch (frontFieldId) {
    case CrmSyncAccountDefaultFieldIdsEnum.DOMAINS:
      return 'mentionSmall';
    case CrmSyncAccountDefaultFieldIdsEnum.NAME:
    case CrmSyncAccountDefaultFieldIdsEnum.DESCRIPTION:
      return 'contentSmall';
    default: {
      // We should only get back the pre-defined ids in CrmSyncAccountDefaultFieldIdsEnum.
      reportToBugsnag(
        new ErrorWithData(
          '[CRM Account Sync] An unknown default field id was passed in to the crm sync page.',
          {frontFieldId},
        ),
      );
      return undefined;
    }
  }
}

export function renderFrontAccountDefaultFieldName(frontFieldId?: number) {
  // We are rendering a default field that is not a true custom field.
  switch (frontFieldId) {
    case CrmSyncAccountDefaultFieldIdsEnum.NAME:
      return <Trans id="crm-syncs-page-mapping-account-default-field-name">Name</Trans>;
    case CrmSyncAccountDefaultFieldIdsEnum.DOMAINS:
      return <Trans id="crm-syncs-page-mapping-account-default-field-domains">Email domains</Trans>;
    case CrmSyncAccountDefaultFieldIdsEnum.DESCRIPTION:
      return <Trans id="crm-syncs-page-mapping-account-default-field-description">Description</Trans>;
    default: {
      // We should only get back the pre-defined fields in CrmSyncAccountDefaultFieldIdsEnum.
      reportToBugsnag(
        new ErrorWithData(
          '[CRM Account Sync] An unknown default field id was passed in to the crm sync page.',
          {frontFieldId},
        ),
      );
      return undefined;
    }
  }
}

export function renderFrontCardDefaultFieldName(frontFieldId?: number) {
  // We are rendering a default field that is not a true custom field.
  switch (frontFieldId) {
    case CrmSyncCardDefaultFieldIdsEnum.NAME:
      return <Trans id="crm-syncs-page-mapping-card-default-field-name">Name</Trans>;
    case CrmSyncCardDefaultFieldIdsEnum.EMAIL:
      return <Trans id="crm-syncs-page-mapping-card-default-field-email">Email</Trans>;
    case CrmSyncCardDefaultFieldIdsEnum.PHONE:
      return <Trans id="crm-syncs-page-mapping-card-default-field-phone">Phone number</Trans>;
    case CrmSyncCardDefaultFieldIdsEnum.ACCOUNT_ID:
      return <Trans id="crm-syncs-page-mapping-card-default-field-account">Account</Trans>;
    case CrmSyncCardDefaultFieldIdsEnum.BIO:
      return <Trans id="crm-syncs-page-mapping-card-default-field-bio">Description</Trans>;
    default: {
      // We should only get back the pre-defined fields in CrmSyncAccountDefaultFieldIdsEnum.
      reportToBugsnag(
        new ErrorWithData('[CRM Card Sync] An unknown default field id was passed in to the crm sync page.', {
          frontFieldId,
        }),
      );
      return undefined;
    }
  }
}

export function computeFrontCardDefaultFieldIcon(frontFieldId?: number) {
  // We are rendering a default field that is not a true custom field.
  switch (frontFieldId) {
    case CrmSyncCardDefaultFieldIdsEnum.EMAIL:
      return defaultIcons.email;
    case CrmSyncCardDefaultFieldIdsEnum.PHONE:
      return defaultIcons.phone;
    case CrmSyncCardDefaultFieldIdsEnum.ACCOUNT_ID:
      return defaultIcons.account;
    case CrmSyncCardDefaultFieldIdsEnum.NAME:
    case CrmSyncCardDefaultFieldIdsEnum.BIO:
      return 'contentSmall';
    default: {
      // We should only get back the pre-defined ids in CrmSyncAccountDefaultFieldIdsEnum.
      reportToBugsnag(
        new ErrorWithData('[CRM Card Sync] An unknown default field id was passed in to the crm sync page.', {
          frontFieldId,
        }),
      );
      return undefined;
    }
  }
}

export function computeCustomFieldResourceType(resourceType: CrmSyncIntegrationResourceTypesEnum) {
  switch (resourceType) {
    case CrmSyncIntegrationResourceTypesEnum.CARD:
      return CustomFieldResourceTypesEnum.CARD;
    case CrmSyncIntegrationResourceTypesEnum.ACCOUNT:
    default:
      return CustomFieldResourceTypesEnum.ACCOUNT;
  }
}
